import { createAsyncThunk } from '@reduxjs/toolkit';
import { Auth } from 'api';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { checkUserRoles, parseError } from '@repo/utils';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useAppDispatch } from 'store/store';
import { saveTempPassword, signIn, storeEmail, UserRoles } from 'store/user';
import { setJWTToken } from 'utils/jwt';

const signInUser = createAsyncThunk(
  'user/signInUser',
  async (params: { email: string; password: string }, { dispatch }) => {
    const cognitoUser = await Auth.signIn(params.email, params.password);

    const hasRequiredRole = checkUserRoles(
      cognitoUser?.user?.roles,
      UserRoles.USER,
    );

    if (!hasRequiredRole) throw new Error('Invalid email or password');

    if (cognitoUser?.token) setJWTToken(cognitoUser.token);

    dispatch(signIn(cognitoUser.user));
  },
);
export const resendSignUp = async (email: string) => {
  await Auth.resendSignUp(email);
  toast('Resent verification email');
};
const useSignIn = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const from = location?.state?.from as string | undefined;
  const { trackEvent } = useAnalytics();

  const signIn = useCallback(
    async (email: string, password: string) => {
      toast.dismiss();
      try {
        await dispatch(signInUser({ email, password })).unwrap();
        trackEvent('Signed In');
        trackEvent('Logged In', { login_method: 'password', source: 'web' });
        if (from) {
          navigate(from, { state: { ...location.state, from: undefined } });
        } else {
          navigate('/', { state: { ...location.state, from } });
        }
      } catch (error) {
        const parsedError = parseError(error);

        if (parsedError.name === 'UserNotConfirmedException') {
          dispatch(storeEmail(email));
          dispatch(saveTempPassword(password));
          await resendSignUp(email);
          navigate('/check-email');
        } else if (parsedError.message) {
          toast.error(parsedError.message);
        } else throw error;
      }
    },
    [dispatch, trackEvent, from, navigate],
  );

  return signIn;
};
export default useSignIn;
